import { createContext, useContext, useEffect, useState, useRef } from 'react'
import { supabase, ADMIN_EMAILS } from '../lib/supabase'

const AuthContext = createContext({})

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  async function fetchProfile(userId, userEmail) {
    if (!userId) return
    const isAdminEmail = ADMIN_EMAILS.includes(userEmail)

    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single()

    if (!mountedRef.current) return

    if (error || !data) {
      const role = isAdminEmail ? 'admin' : 'writer_blog'
      const { data: created } = await supabase
        .from('profiles')
        .insert({ id: userId, email: userEmail, role, display_name: userEmail.split('@')[0] })
        .select()
        .single()
      if (mountedRef.current) setProfile(created || null)
    } else if (isAdminEmail && data.role !== 'admin') {
      const { data: updated } = await supabase
        .from('profiles')
        .update({ role: 'admin' })
        .eq('id', userId)
        .select()
        .single()
      if (mountedRef.current) setProfile(updated || { ...data, role: 'admin' })
    } else {
      if (mountedRef.current) setProfile(data)
    }
  }

  useEffect(() => {
    // Initial session is resolved explicitly via getSession() so the logged-in
    // state renders on first paint — no manual refresh needed. The
    // onAuthStateChange listener below then handles only *subsequent* changes
    // (sign in/out, token refresh). INITIAL_SESSION is intentionally ignored
    // there so it doesn't race with this getSession() call.
    //
    // Critical: never `await` a Supabase data call *inside* the
    // onAuthStateChange callback. The SDK holds an auth lock while the callback
    // runs, and a query (which needs that lock to attach its token) would
    // deadlock — that was the "logged out until refresh" bug. So profile
    // fetches in the listener are deferred and never awaited.
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mountedRef.current) return
      if (session?.user) {
        setUser(session.user)
        fetchProfile(session.user.id, session.user.email)
      } else {
        setUser(null)
        setProfile(null)
      }
      setLoading(false)
    }).catch(() => {
      if (mountedRef.current) setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mountedRef.current) return

        // Resolved by getSession() above — ignore to prevent a double-set race.
        if (event === 'INITIAL_SESSION') return

        if (event === 'SIGNED_IN') {
          setUser(session?.user ?? null)
          if (session?.user) {
            // Defer out of the callback so the auth lock is released first.
            setTimeout(() => {
              if (mountedRef.current) fetchProfile(session.user.id, session.user.email)
            }, 0)
          }
          setLoading(false)
          return
        }

        if (event === 'SIGNED_OUT') {
          setUser(null)
          setProfile(null)
          setLoading(false)
          return
        }

        if (event === 'TOKEN_REFRESHED' && session?.user) {
          // Session refreshed silently — update user, keep existing profile.
          setUser(session.user)
          return
        }
      }
    )

    // Safety net — if getSession() somehow stalls (e.g. network), unblock the UI.
    const timeout = setTimeout(() => {
      if (mountedRef.current) setLoading(false)
    }, 3000)

    return () => {
      subscription.unsubscribe()
      clearTimeout(timeout)
    }
  }, [])

  // Keep profile fresh via realtime
  useEffect(() => {
    if (!user) return
    const channel = supabase
      .channel('profile-live-' + user.id)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'profiles',
        filter: `id=eq.${user.id}`,
      }, ({ new: updated }) => {
        if (updated && mountedRef.current) {
          setProfile(prev => ({ ...prev, ...updated }))
        }
      })
      .subscribe()
    return () => supabase.removeChannel(channel)
  }, [user])

  async function signUp(email, password, displayName) {
    const { error } = await supabase.auth.signUp({
      email, password,
      options: { data: { display_name: displayName } }
    })
    return { error }
  }

  async function signIn(email, password) {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    return { error }
  }

  async function signInWithGoogle() {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin }
    })
    return { error }
  }

  async function signOut() {
    setUser(null)
    setProfile(null)
    await supabase.auth.signOut()
  }

  return (
    <AuthContext.Provider value={{
      user, profile, loading,
      signUp, signIn, signInWithGoogle, signOut,
      refreshProfile: () => user
        ? fetchProfile(user.id, user.email)
        : Promise.resolve()
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
